import React, { useRef } from "react";
import { Grid } from "@mui/material";
import { Alert, Box, Card, Chip, Pagination, Stack } from "@curves/ui-base";
import { EditableInfo, OverflowMenu, PageSortAndTotalBox } from "@icoach/components";
import { formatCurrencyFn, formatCardExpiryDate, isFunction, isEmpty, isEqual } from "@util/utils";
import { displayDateText } from "@util/moment";
import { CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import useMemberDeviceOrderApi from "@apis/useMemberDeviceOrderApi";
import useOverview from "@util/hook/useOverview";
import { Link } from "react-router-dom";
import SmartFitnessBandDialogBox from "@icoach/smartFitnessBand/dialog/SmartFitnessBandDialogBox";
import { useMemberDetailContext } from "@icoach/memberDetail/MemberDetailContext";
import clsx from "clsx";

/**
 * 根據訂閱狀態編號，取得對應 Chip 顏色
 * @param {number} status
 * @returns {"success"|"warning"|"error"|"info"}
 */
const getOrderStatusColor = (status) => {
    switch (status) {
        case 1:
            return "success";
        case 2:
            return "warning";
        case 3:
        case 9:
            return "error";
        default:
            return "info";
    }
}

/**
 * 訂閱資訊欄位定義清單，用於渲染 <EditableInfo />
 *
 * @typedef {{ id: string, label: string, formatter?: (value: any, item?: object) => React.ReactNode }} InfoField
 *
 * @type {InfoField[]}
 */
const subscriptionInfoFields = [
    {id: "deviceOrderNo", label: "主單編號"},
    {id: "monthFee", label: "訂閱金額", formatter: v => formatCurrencyFn(v, {symbol: "$"})},
    {
        id: "deviceOrderStatusText",
        label: "訂閱狀態",
        formatter: (v, raw = {}) => <Chip label={v} color={getOrderStatusColor(raw?.deviceOrderStatus)}/>
    },
    {id: "cardExpiryDate", label: "信用卡效期", formatter: v => formatCardExpiryDate(v)},
    {id: "startDate", label: "訂閱起始日", formatter: v => displayDateText(v)},
    {id: "endDate", label: "訂閱終止日", formatter: v => displayDateText(v)}
];

/**
 * 產生單筆訂閱資料的操作選單（如 查看、變更卡片、退訂、刪除）
 *
 * @param {object} itemData - 單筆訂閱資料
 * @param {object} meta - 額外資料，例如 dialogRef, refresh function, memberProfile
 * @returns {object[]} menuData - 滿足 OverflowMenu 所需的 menu 結構
 */
const getItemMenu = (itemData, meta) => {
	const { dialogRef, refresh, memberProfile } = meta || {};
	const { openSmartFitnessBandEditCreditCardDialog, openSmartFitnessBandDeleteContractDialog } = dialogRef.current || {};

	const actions = [
		{
			text: "查看",
			condition: true,
			to: CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.CONTRACT_DETAIL_MAIN_PATH(itemData.deviceOrderID),
			component: Link,
		},
		{
			text: "變更信用卡",
			condition: true,
			onClick: () =>
				isFunction(openSmartFitnessBandEditCreditCardDialog) &&
				openSmartFitnessBandEditCreditCardDialog?.(
					{
						memberID: memberProfile?.memberID,
						memberNo: memberProfile?.memberNo,
						memberName: memberProfile?.memberName,
						memberPaymentID: itemData?.memberPaymentID,
					},
					refresh,
				),
		},
		{
			text: "退訂",
			condition: itemData?.canQuit,
			to: CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.CONTRACT_QUIT_PATH(itemData.deviceOrderID),
			component: Link,
		},
		{
			text: "刪除",
			condition: itemData?.canDelete,
			onClick: () =>
				isFunction(openSmartFitnessBandDeleteContractDialog) &&
				openSmartFitnessBandDeleteContractDialog(
					{
						deviceOrderID: itemData?.deviceOrderID,
						displayName: memberProfile?.memberName,
						nickName: memberProfile?.nickName,
					},
					refresh,
				),
		},
	];
	return actions
		.filter(action => action.condition)
		.map(actions => {
			const _action = { ...actions };
			delete _action.condition;
			return _action;
		});
};

/**
 * 判斷該訂閱是否為「無效」訂單（status: 3 = 已退訂, 9 = 已刪除）
 *
 * @param {number} deviceOrderStatus
 * @returns {boolean}
 */
const isInvalidOrder = (deviceOrderStatus) => [3, 9].some(status => isEqual(deviceOrderStatus, status));

/**
 * 會員詳細頁 > 智能系統 > 合約資訊
 *
 * @component
 * @param {{ memberID: string }} props
 */
const MemberDetailSmartFitnessBandContentDocument = ({memberID}) => {
    const {postMemberDeviceOrderListApi} = useMemberDeviceOrderApi();
    const {overviewData, searchParams, updateOverviewPage, getOverviewData} = useOverview({
        getOverviewApi: postMemberDeviceOrderListApi,
        defaultSearchParams: {
            memberID,
            status: 99,
            pageIndex: 1,
            pageSize: 10
        },
        enableInfiniteScroll: false,
        isFetchOnMount: true,
        enableQueryString: false
    });
    const {list = [], pagination} = overviewData || {};

    const {memberProfile} = useMemberDetailContext();

    const dialogRef = useRef(null);
    const refresh = () => getOverviewData(searchParams);

    return (
        <Box>

            {
                isEmpty(list) ? (
                    <Alert className="mb-4" color="info" severity="info" variant="filled">
                        尚無資料
                    </Alert>
                ) : (
                    <>
                        <Stack direction="row-reverse" className={"mb-2"}>
                            <PageSortAndTotalBox pageSize={pagination.pageSize} pageIndex={pagination.pageIndex}
                                                 totalCount={pagination.totalCount}/>
                        </Stack>
                        {
                            list.map(item => (
                                <Card key={item.deviceOrderID}
                                      className={clsx("px-4 py-3 smart-fitness-band-doc-card mb-2", {quit: isInvalidOrder(item.deviceOrderStatus)})}>
                                    <OverflowMenu actionButtonProps={{className: "smart-fitness-band-doc-card-menu"}}
                                                  menuData={getItemMenu(item, {dialogRef, refresh, memberProfile})}/>
                                    <Grid spacing={0} container>
                                        {
                                            subscriptionInfoFields.map(({label, formatter, id}) => {
                                                const itemValue = item[id];
                                                return (
                                                    <Grid key={id} xs={6} item>
                                                        <EditableInfo label={label}>
                                                            {(() => {
                                                                try {
                                                                    return isFunction(formatter)
                                                                        ? formatter(itemValue, item)
                                                                        : (itemValue ?? "-");
                                                                } catch (e) {
                                                                    console.error(`欄位 ${id} formatter 執行錯誤`, e);
                                                                    return "-";
                                                                }
                                                            })()}
                                                        </EditableInfo>
                                                    </Grid>
                                                );
                                            })
                                        }
                                    </Grid>
                                </Card>
                            ))
                        }
                        <Stack direction="row-reverse" className="mt-4">
                            <Pagination count={pagination?.totalPage} page={pagination?.pageIndex} color="secondary"
                                        shape="rounded"
                                        onChange={(e, pageIndex) => updateOverviewPage(pageIndex)}/>
                        </Stack>
                    </>
                )
            }
            <SmartFitnessBandDialogBox ref={dialogRef}/>
        </Box>
    );
};

export default MemberDetailSmartFitnessBandContentDocument;
